import apiClient from '../server/api.config';

export interface PrivilegeBenefit {
  id: number;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  compagnie_id: number;
  name: string;
  description: string;
  type: 'discount' | 'voucher' | 'gift';
  value?: number;
  gift_content?: string;
  conditions?: string;
  flash_banner_text?: string;
  start_date?: string;
  end_date?: string;
  active: boolean;
  eligible_user_type: 'partner' | 'member' | 'both';
  eligible_company_ids?: number[];
}

export interface Privilege {
  id: number;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  name: string;
  description: string;
  advantage: string;
  is_active: boolean;
  compagny_id?: number; // Can be extracted from compagny.id if relation is loaded
  compagny?: {
    id: number;
    [key: string]: any;
  };
  passkit_tier_id?: string;
  employees?: Array<Employee>;
  image_url?: string;
  address?: string;
  phone?: string;
  website_link?: string;
  facebook_link?: string;
  instagram_link?: string;
  opening_hours?: {
    weekdays?: { open?: string; close?: string };
    fridayAndErevYomTov?: { open?: string; close?: string };
  };
  benefits?: PrivilegeBenefit[];
}

interface Employee {
  id: number;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  identity_card_id: string | null;
  first_name: string;
  last_name: string;
  email: string;
  start_date: string;
  end_date: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
  image_url: string;
  birth_date: string;
  active: boolean;
  privileges: Array<Privilege>;
}

interface EmployeeResponse {
  record: Employee;
}

export const getEmployeeById = async (employeeId: string) => {
  const response = await apiClient.get<EmployeeResponse>(
    `/employee/${Number(employeeId)}`
  );
  return response.data.record;
};

export const generatePassForEmployee = async (employeeId: string) => {

  const response = await apiClient.post(
    `/employee/${Number(employeeId)}/generate-pass`
  );
  return response.data;
};

interface EmployeeInfoResponse {
  record: {
    first_name: string;
    last_name: string;
    privilegeIds: number[];
    pass_id?: string;
    user_type?: string;
    identity_card_id?: string;
    compagny_id?: number;
    compagny?: {
      id: number;
      name: string;
    };
  };
}

interface PassStatusResponse {
  record: {
    status?: string; // Member status (e.g., "ENROLLED")
    passMetaData?: {
      status?: string; // Pass status (e.g., "PASS_ISSUED")
      [key: string]: any;
    };
    [key: string]: any;
  };
}

export const getEmployeeInfo = async (employeeId: string) => {
  const response = await apiClient.get<EmployeeInfoResponse>(
    `/employee/${Number(employeeId)}/info`
  );
  return response.data.record;
};

interface EmployeeNameResponse {
  record: {
    first_name: string;
    last_name: string;
  };
}

export const getEmployeeName = async (employeeId: string) => {
  const response = await apiClient.get<EmployeeNameResponse>(
    `/employee/${Number(employeeId)}/name`
  );
  return response.data.record;
};

interface PrivilegeIdsResponse {
  records?: number[];
  record?: number[];
}

export const getEmployeePrivilegeIds = async (employeeId: string): Promise<number[]> => {
  const response = await apiClient.get<PrivilegeIdsResponse>(
    `/employee/${Number(employeeId)}/privilege-ids`
  );
  // Backend wraps arrays in { records: [...] }
  return response.data.records || response.data.record || (Array.isArray(response.data) ? response.data : []);
};

interface PrivilegesResponse {
  records?: Privilege[];
  total_size?: number;
}

export const getPrivilegesByIds = async (ids: number[]): Promise<Privilege[]> => {
  if (!ids || ids.length === 0) {
    return [];
  }
  const response = await apiClient.post<PrivilegesResponse>(
    `/privileges/by-ids`,
    { ids }
  );
    // Backend wraps arrays in { records: [...] }
  return response.data.records || (Array.isArray(response.data) ? response.data : []);
};

export const getAllPrivileges = async (employeeId: number): Promise<Privilege[]> => {
  const response = await apiClient.get<PrivilegesResponse>(
    `/privileges?employee_id=${employeeId}`
  );
  // Backend wraps arrays in { records: [...] } or returns direct array
  return response.data.records || (Array.isArray(response.data) ? response.data : []);
};

export const getPassStatus = async (passId: string): Promise<PassStatusResponse> => {

  const response = await apiClient.get<PassStatusResponse>(
    `/pass/status?passId=${passId}`
  );
  return response.data;
};

interface EmployeeInfoByPassIdResponse {
  record?: {
    id: number;
    first_name: string;
    last_name: string;
    user_type?: string;
    identity_card_id?: string;
    compagny_id?: number;
    benefits?: PrivilegeBenefit[];
    [key: string]: any;
  };
}

type EmployeeInfoByIdentityCard = {
  id: number;
  first_name: string;
  last_name: string;
  user_type?: string;
  identity_card_id?: string;
  compagny_id?: number;
  compagny?: {
    id: number;
    name: string;
    image_url?: string;
  };
  [key: string]: any;
};

export const getEmployeeInfoByIdentityCardId = async (
  identityCardId: string,
): Promise<EmployeeInfoByIdentityCard | null> => {
  try {
    const response = await apiClient.get<EmployeeInfoByIdentityCard>(
      `/employee/info-by-identity-card/${identityCardId}`,
    );

    return response.data;
  } catch (error) {
    console.error(
      "[EmployeeService] getEmployeeInfoByIdentityCardId error:",
      error
    );
    return null;
  }
};

export const getPassOwnerInfoAndBenefits = async (compagnyId: number, passId: string) => {
  try {
    const response = await apiClient.get<EmployeeInfoByPassIdResponse>(
      `/employee/owner-info-and-benefits?compagnyId=${compagnyId}&passId=${passId}`
    );
    return response.data;
  } catch (error) {
    console.error(
      "[EmployeeService] getPassOwnerInfoAndBenefits error:",
      error
    );
    throw error;
  }
};